/**
 * ReviewMetadataPage
 * ------------------
 * After sign-in, if the user has previously submitted metadata we
 * show a quick **“Have any of these details changed?”** check:
 *
 * • Fetch most-recent JSON from `user_metadata/{uid}/{timestamp}.json`.
 * • Render a compact <ReviewStep/> preview, filtered by the usual
 *   `questions` + `filterQuestions()` pipeline.
 * • “No (continue)”  → skip straight to the recording flow.
 * • “Yes (update)”   → open the full metadata wizard with fields
 *   pre-filled (`navigate("/form", { state: { prefill } })`).
 */

import React, { useEffect, useState, memo } from "react";
import { useNavigate } from "react-router-dom";

import ReviewStep from "../components/form/ReviewStep";
import Spinner from "../components/Spinner";

import { questions } from "../utils/form/formQuestions";
import { filterQuestions } from "../utils/form/formRuntime";
import { fetchLatestMetadata } from "../utils/firebase/fetchLatestMetadata";
import { useAuth } from "../context/AuthContext";

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function ReviewMetadataPage() {
  const { user } = useAuth();               // Firebase user (context)
  const navigate = useNavigate();

  const [formData,   setFormData] = useState(null); // null = loading
  const [filteredQs, setFiltered]  = useState([]);  // filtered list

  /* ── On mount: try to load the newest metadata file ─────────────── */
  useEffect(() => {
    if (!user?.uid) return;                 // protected route safeguard

    fetchLatestMetadata(user.uid)
      .then((data) => {
        if (!data) {
          // First-time user → go straight to wizard
          navigate("/form", { replace: true });
          return;
        }

        setFormData(data);
        setFiltered(filterQuestions(questions, data, user));
      })
      .catch((err) => {
        console.error("Could not load previous metadata:", err);
        navigate("/form", { replace: true });  // fail-safe
      });
  }, [user, navigate]);

  /* ------------------------------ UI ------------------------------ */
  if (formData === null) {
    return <Spinner />;                     // initial loading state
  }

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-start items-center gap-6">
      <ReviewStep
        title="Have any of these details changed?"
        questions={filteredQs}
        formData={formData}
      />

      <div className="flex w-full justify-between pt-4">
        {/* Skip → keep previous answers */}
        <button
          type="button"
          className="outlined-button"
          onClick={() => navigate("/recording")}
        >
          No (continue)
        </button>

        {/* Update → open wizard with pre-filled answers */}
        <button
          type="button"
          className="primary-button"
          onClick={() => navigate("/form", { state: { prefill: formData } })}
        >
          Yes (update)
        </button>
      </div>
    </div>
  );
}

export default memo(ReviewMetadataPage);